import React, { useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import GlobalDialog from '../../../../../components/mui/Dialogue/GlobalDialog';
import { CancelButton, SubmitButton } from '../../../../../components/mui/Buttons/Buttons';
import { updateTerminationData } from '../../../../../redux/features/CRDslice';

/**
 * Sets one enrollment back to active.
 *
 * PUT admission-register/update-termination-clearance-data/:course_enrollment_id
 * with { course_enrollment_status: 1 }. That endpoint's status-1 branch reads
 * nothing else off the body - it flips the status and re-activates the trainer
 * assignments - so there is nothing to collect here beyond the confirmation.
 */
const ActivateStudentDialog = ({ open, setOpen, row, onSuccess }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!row?.course_enrollment_id) return;
    setSubmitting(true);
    try {
      const res = await dispatch(
        updateTerminationData({
          course_enrollment_id: row.course_enrollment_id,
          course_enrollment_status: 1,
        }),
      );

      if (res?.payload?.status === 'success') {
        enqueueSnackbar(res.payload.message || 'Student is now active', { variant: 'success' });
        setOpen(false);
        onSuccess?.();
      } else {
        enqueueSnackbar(res?.payload?.message || 'Could not activate the student', { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GlobalDialog
      title={'Make Student Active'}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="sm"
      closeButton={true}
      content={
        <Stack gap={2}>
          <Typography variant="body1">
            Set <b>{row?.student_name || '—'}</b>
            {row?.admn_no ? ` (${row.admn_no})` : ''} back to <b>active</b>?
          </Typography>

          <Stack gap={0.5}>
            <Typography variant="body2" color="text.secondary">
              Course: {row?.course_name || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Current status: {row?.course_status || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Owner: {row?.owner || '—'}
            </Typography>
          </Stack>

          <Alert severity="info">
            This also re-activates the trainer assignments on the course. It does not change who the student is
            assigned to — use <b>Assign CRO</b> for that.
          </Alert>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Stack direction={'row'} spacing={1}>
              <CancelButton size="small" action={() => setOpen(false)}>
                Cancel
              </CancelButton>
              <SubmitButton size="small" disabled={submitting} action={handleConfirm}>
                {submitting ? 'Please wait...' : 'Make Active'}
              </SubmitButton>
            </Stack>
          </Box>
        </Stack>
      }
    />
  );
};

export default React.memo(ActivateStudentDialog);
